import { useTenant } from "hooks/settings";
import {
  MaterialRead,
  MixRead,
  useGetMaterialMetadataSetQuery,
} from "src/store/api/generatedApi";
import { useCallback, useMemo } from "react";
import { HeatMaterial } from "../components/strategyDashboard/heats/types";
import { useTheme } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { aggregateBasketLayersByMaterial } from "components/common/plan/utils";

export const UNKNOWN_MATERIAL_NAME = "unknown";

export const useMaterials = () => {
  const tenantName = useTenant();
  const response = useGetMaterialMetadataSetQuery({
    tenantName,
    materialMetadataSetId: "default",
  });
  return {
    materials: response.data?.materials,
    isLoading: response.isLoading,
    isError: response.isError,
  };
};

export const useBasketMaterials = (): MaterialRead[] => {
  const { materials } = useMaterials();
  return useMemo(
    () =>
      (materials ?? [])
        .filter(
          (material) =>
            material.addition_location === "basket" &&
            material.visibility === "visible"
        )
        .sort(
          (left, right) =>
            left.deployment_ordering - right.deployment_ordering ||
            left.name.localeCompare(right.name)
        ),
    [materials]
  );
};

export const useBasketOneMaterials = (mix: MixRead) => {
  const { materials } = useMaterials();
  const orderingByName = useMemo(() => {
    const lookup: Record<string, number> = {};
    (materials ?? []).forEach((material) => {
      lookup[material.name] = material.ordering;
    });
    return lookup;
  }, [materials]);
  return useMemo(() => {
    const basketOne = mix.baskets.find((basket) => basket.basket_number === 1);
    return aggregateBasketLayersByMaterial(
      basketOne?.layers ?? [],
      orderingByName
    );
  }, [mix.baskets, orderingByName]);
};

const createMaterialColorMap = (
  materials: MaterialRead[]
): Record<string, string> => {
  const colorMap: Record<string, string> = {};
  materials.forEach((material) => {
    colorMap[material.name] = material.colour;
  });
  return colorMap;
};

export const useMaterialColor = () => {
  const { materials } = useMaterials();
  const theme = useTheme();
  const colorMap = useMemo(
    () => createMaterialColorMap(materials ?? []),
    [materials]
  );
  const getColor = useCallback(
    (materialName: string, ignored?: boolean) => {
      if (ignored) {
        return theme.palette.grey[300];
      }
      if (materialName === UNKNOWN_MATERIAL_NAME) {
        return theme.palette.grey[800];
      }
      return colorMap[materialName] ?? theme.palette.common.black;
    },
    [colorMap, theme]
  );
  const addColors = useCallback(
    (materials: HeatMaterial[]) => {
      return materials.map((material) => ({
        ...material,
        color: getColor(material.name, material.ignored),
      }));
    },
    [getColor]
  );
  return { getColor, addColors };
};

export const useMaterialDisplayName = () => {
  const { t } = useTenantTranslation();
  return useCallback(
    (materialName: string) =>
      materialName === UNKNOWN_MATERIAL_NAME ? t("unknown") : materialName,
    [t]
  );
};

export const useMaterialIdToData = () => {
  const { materials } = useMaterials();
  return useMemo(
    () =>
      materials?.reduce<Record<number, MaterialRead>>(
        (acc, material) => ({ ...acc, [material.id]: material }),
        {}
      ),
    [materials]
  );
};
